import CommentProps from "@/components/_types/comments-props";
import { formatTimestamp } from "@/components/util/date-utils";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";
import Image from "next/image";
import { useEffect, useState } from "react";
import {
	FaRegThumbsDown,
	FaRegThumbsUp,
	FaThumbsDown,
	FaThumbsUp,
} from "react-icons/fa";
import { VoteType } from "../enums/vote-type";

interface CommentComponentProps extends CommentProps {
	addReply: (parentId: string, comment: string) => void;
	user: any;
	refresh: any;
}

const CommentComponent = ({
	id,
	comment,
	likeCount,
	dislikeCount,
	replies = [],
	depth = 0,
	maxDepth = 4,
	author,
	isLiked,
	isDisliked,
	date,
	addReply,
	user,
	refresh,
}: CommentComponentProps) => {
	const { addToast } = useToast();
	const { call } = useApi();
	const [replyText, setReplyText] = useState("");
	const [showReplyInput, setShowReplyInput] = useState(false);
	const [marginRight, setMarginRight] = useState("0");

	useEffect(() => {
		if (typeof window !== "undefined") {
			setMarginRight(
				depth > 0
					? `${
							Math.min(depth, 3) *
							(window.innerWidth < 640
								? 15
								: window.innerWidth < 768
								? 25
								: window.innerWidth < 1024
								? 35
								: 50)
					  }px`
					: "0"
			);
		}
	}, [depth]);

	const handleVote = async (type: VoteType) => {
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}

		await call(
			`/api/comments/vote`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					commentId: id,
					voteType: type,
				}),
			},
			true
		);

		await refresh();
	};

	const handleRemoveVote = async () => {
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}

		await call(
			`/api/comments/vote`,
			{
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					commentId: id,
				}),
			},
			true
		);

		await refresh();
	};

	const handleReply = () => {
		if (replyText.trim()) {
			addReply(id, replyText);
			setReplyText("");
			setShowReplyInput(false);
		}
	};

	return (
		<div className="w-full">
			<div
				className="text-white p-4 mb-4 relative border-b border-white/[12%]"
				style={{ marginRight }}
			>
				<div className="flex items-center justify-start gap-2 mb-2">
					<div className="w-[32px] h-[32px] rounded-full overflow-hidden">
						<Image
							src="/icon-character.svg"
							alt="Avatar"
							width={32}
							height={32}
						/>
					</div>
					<p className="text-[14px] font-normal">{author}</p>
					<p className="text-white/60">{formatTimestamp(date)}</p>
				</div>
				<p className="text-sm mb-2">{comment}</p>
				<div className="flex items-center gap-2">
					<button
						onClick={() =>
							isLiked ? handleRemoveVote() : handleVote(VoteType.LIKE)
						}
						className={"text-white"}
					>
						{isLiked ? <FaThumbsUp size={16} /> : <FaRegThumbsUp size={16} />}
					</button>
					<span className="text-gray-400 text-sm">{likeCount}</span>
					<button
						onClick={() =>
							isDisliked ? handleRemoveVote() : handleVote(VoteType.DISLIKE)
						}
						className={"text-white"}
					>
						{isDisliked ? (
							<FaThumbsDown size={16} />
						) : (
							<FaRegThumbsDown size={16} />
						)}
					</button>
					<span className="text-gray-400 text-sm">{dislikeCount}</span>
					<button
						onClick={() => setShowReplyInput(!showReplyInput)}
						className="text-white/60 text-[10px]"
					>
						پاسخ دادن
					</button>
				</div>

				{/* Reply Input */}
				{showReplyInput && (
					<div className="mt-2 relative">
						<textarea
							rows={6}
							placeholder="پاسخ..."
							className="w-full bg-[#1A111F] border border-black text-white p-2 pl-16 rounded text-sm"
							value={replyText}
							onChange={(e) => setReplyText(e.target.value)}
						/>
						<button
							onClick={handleReply}
							className="font-semiBold text-[16px] text-custom-pink absolute left-3 bottom-3 comment-button hover:opacity-70"
						>
							ثبت پاسخ
						</button>
					</div>
				)}
			</div>

			{replies && replies.length > 0 && (
				<div>
					{replies.map((reply) => (
						<CommentComponent
							key={reply.id}
							{...reply}
							depth={depth + 1}
							maxDepth={maxDepth}
							addReply={addReply}
							user={user}
							refresh={refresh}
						/>
					))}
				</div>
			)}
		</div>
	);
};

const Comments = ({
	comments,
	refresh,
	user,
	itemId,
	type,
}: {
	comments: any[];
	refresh: any;
	user: any;
	itemId: string;
	type: "NEWS" | "CONTENT";
}) => {
	const [newCommentText, setNewCommentText] = useState("");
	const { addToast } = useToast();
	const { call } = useApi();

	const addComment = async () => {
		if (!newCommentText || newCommentText === "") {
			addToast("متن را وارد نمایید", "info");
			return;
		}
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}
		await call(
			`/api/comments?type=${type}`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					comment: newCommentText,
					[type === "CONTENT" ? "contentId" : "newsId"]: itemId,
				}),
			},
			true
		);
		await refresh();
		setNewCommentText("");
	};

	const addReply = async (commentId: number, comment: string) => {
		if (!comment || comment === "") {
			addToast("متن را وارد نمایید", "info");
			return;
		}
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}
		await call(
			`/api/comments?type=${type}`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					parentId: commentId,
					comment: comment,
					[type === "CONTENT" ? "contentId" : "newsId"]: itemId,
				}),
			},
			true
		);
		await refresh();
		setNewCommentText("");
	};

	return (
		<div className="mx-auto bg-black/90 p-6 rounded-xl w-full" id="comments">
			<h2 className="text-white text-xl font-bold my-[28px] text-right md:text-[32px]">
				دیدگاه ها ({comments.length ?? 0})
			</h2>
			<div className="mt-2 relative">
				<textarea
					rows={6}
					placeholder="ثبت دیدگاه..."
					className="w-full bg-[#1A111F] border border-black text-white p-2 pl-16 rounded text-sm min-h-[240px]"
					value={newCommentText}
					onChange={(e) => setNewCommentText(e.target.value)}
				/>
				<button
					onClick={addComment}
					className="font-semiBold text-[16px] text-custom-pink absolute left-3 bottom-3 comment-button hover:opacity-70"
				>
					ثبت دیدگاه
				</button>
			</div>
			<div>
				{comments.map((comment) => (
					<CommentComponent
						key={comment.id}
						{...comment}
						addReply={addReply}
						user={user}
						refresh={refresh}
					/>
				))}
			</div>
		</div>
	);
};

export default Comments;
